import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { utcGunAySaat } from '../utcTarih';
import { SAYILI, okumaHataMetni } from './kesikOkuma';   // 24 ay şartı — düşen/kesik okuma tek tanım (6 Eki 2026)

// ═════════════════════════════════════════════════════════════════
// FTL NIGHTLY CHECK — süper admin ana ekranı (3 Eki 2026, FTL adım 6b)
//
// 🔑 Serkan: gece taraması uyarısı **A** — süper admin panelinde tek satır; SÜPER ADMIN UYARI MERKEZİ kurulunca oraya
//    taşınır. Tasarım SYSTEM CHECKS'in aynısı (tek tip): başarıyı VE SESSİZLİĞİ gösterir — hiç koşmamış iş "yolunda"
//    gibi görünmez (İlke 1).
// Kaynak: `ftl_hesap_defteri` (kaynak = NIGHTLY) — gece işi (`ftl_gece_taramasi`, 00:20 UTC) her şirket için bir satır
//    açar, sunucu sonucu yazar (`sonuc`, `yazilan`, `hata`), kaçmış hesap sayısı `kacan_hesap`.
// DURUM (şirket başına en son gece satırı):
//   KIRMIZI — sunucu hata döndü / çağrı gönderilemedi / 15 dk'da sonuç gelmedi
//   AMBER   — hiç koşmadı · son koşu 26 saatten eski (günlük iş + 2 saat pay) · kaçmış hesap var (gece düzeltti ama
//             tetikleyici yolu bir yerde aksıyor)
//   YEŞİL   — ok
// ═════════════════════════════════════════════════════════════════

export const GECE_ESIK_SAAT = 26;
const SONUC_BEKLEME_DK = 15;
const RENK = { yesil: 'var(--green)', kirmizi: 'var(--red)', amber: 'var(--orange)' };
const SIRA = { yesil: 0, amber: 1, kirmizi: 2 };

const saatOnce = (iso, simdi) => (simdi - new Date(iso).getTime()) / 3600000;
const nekadarOnce = (iso, simdi) => {
  const dk = (simdi - new Date(iso).getTime()) / 60000;
  if (dk < 60) return `${Math.max(0, Math.round(dk))} min ago`;
  if (dk < 1440) return `${Math.round(dk / 60)} h ago`;
  return `${Math.round(dk / 1440)} d ago`;
};

/** Saf: gece satırları + şirketler → { renk, metin, satirlar: [{ ad, metin, renk }] }. */
export function geceDurumu(satirlar, sirketler, simdi = Date.now()) {
  const ad = (id) => (sirketler || []).find(c => c.id === id)?.company_name || String(id).slice(0, 8);
  const son = {};
  (satirlar || []).forEach(r => {
    if (!son[r.customer_id] || r.created_at > son[r.customer_id].created_at) son[r.customer_id] = r;
  });
  const liste = Object.values(son).sort((a, b) => ad(a.customer_id).localeCompare(ad(b.customer_id)));
  if (!liste.length) {
    return { renk: 'amber', metin: 'NEVER RAN', satirlar: [],
             not: 'The nightly check has not run yet. It runs at 00:20 UTC once the database job is installed.' };
  }
  const out = liste.map(r => {
    const zaman = `${utcGunAySaat(r.created_at, { z: true })} (${nekadarOnce(r.created_at, simdi)})`;
    let renk = 'yesil', durum;
    if (r.sonuc === 'ok') durum = `OK · ${r.yazilan ?? 0} dut${(r.yazilan ?? 0) === 1 ? 'y' : 'ies'} changed`;
    else if (r.sonuc) { renk = 'kirmizi'; durum = `${String(r.sonuc).toUpperCase()}${r.hata ? ` — ${r.hata}` : ''}`; }
    else if (saatOnce(r.created_at, simdi) * 60 > SONUC_BEKLEME_DK) { renk = 'kirmizi'; durum = 'NO RESULT — the server did not report back'; }
    else durum = 'RUNNING';
    if ((r.kacan_hesap || 0) > 0) {
      durum += ` · ${r.kacan_hesap} missed recalculation${r.kacan_hesap === 1 ? '' : 's'} (covered by this run)`;
      if (renk === 'yesil') renk = 'amber';
    }
    if (saatOnce(r.created_at, simdi) > GECE_ESIK_SAAT) {
      durum += ` · LAST RUN OVER ${GECE_ESIK_SAAT} H AGO`;
      if (renk === 'yesil') renk = 'amber';
    }
    return { ad: ad(r.customer_id), metin: `${zaman} · ${durum}`, renk };
  });
  const renk = out.reduce((a, x) => (SIRA[x.renk] > SIRA[a] ? x.renk : a), 'yesil');
  const metin = renk === 'yesil' ? 'ALL COMPANIES CHECKED' : renk === 'amber' ? 'NEEDS A LOOK' : 'FAILING';
  return { renk, metin, satirlar: out };
}

export default function FtlGeceDurumu() {
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState('');
  // 🔴 6 Eki 2026 (24 ay şartı, 2d-3 — Serkan A): her şirketin en son gece satırı eskiden SON 300 satırın içinden
  //    aranıyordu; şirket ve gün sayısı arttıkça bir şirketin son koşusu pencerenin dışında kalır, o şirket listeden
  //    düşer ya da (hepsi dışarıdaysa) "NEVER RAN" denirdi. Şimdi her şirket için EN SON satır ayrı sorulur (tek satır).
  useEffect(() => {
    (async () => {
      const sir = await supabase.from('customers').select('id,company_name', SAYILI);
      const sirHata = okumaHataMetni(sir);
      // Okunamadıysa "hiç koşmadı" gibi davranılmaz — ayrı durum (İlke 1).
      if (sirHata) { setHata(sirHata); setVeri({ satirlar: [], sirketler: [] }); return; }
      const sonlar = await Promise.all((sir.data || []).map(c => supabase.from('ftl_hesap_defteri')
        .select('customer_id,created_at,sonuc,yazilan,hata,kacan_hesap,bitti_at')
        .eq('kaynak', 'NIGHTLY').eq('customer_id', c.id).order('created_at', { ascending: false }).limit(1)));
      const dusen = sonlar.find(s => s.error);
      if (dusen) { setHata(dusen.error.message); setVeri({ satirlar: [], sirketler: [] }); return; }
      setVeri({ satirlar: sonlar.flatMap(s => s.data || []), sirketler: sir.data || [] });
    })();
  }, []);
  if (!veri) return null;
  const d = hata ? { renk: 'amber', metin: 'CANNOT READ THE NIGHTLY CHECK', satirlar: [],
                     not: `The nightly check results could not be read (${hata}). The check may still be running fine.` }
    : geceDurumu(veri.satirlar, veri.sirketler);
  return (
    <div data-testid="ftl-gece-durumu"
         style={{ border:`1px solid var(--border)`, borderRadius:8, background:'var(--bg2)', marginBottom:16, overflow:'hidden' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
                    borderBottom:'1px solid var(--border)', background:'var(--bg3)' }}>
        <span style={{ width:9, height:9, borderRadius:9, background:RENK[d.renk], flexShrink:0 }} />
        <span style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--t2)' }}>FTL NIGHTLY CHECK</span>
        <span data-testid="ftl-gece-metin" style={{ fontSize:10, letterSpacing:1, color:RENK[d.renk], fontFamily:'var(--mono)' }}>{d.metin}</span>
      </div>
      <div style={{ padding:'10px 14px', fontSize:11, color:'var(--t2)', lineHeight:1.6 }}>
        {d.not && <div style={{ color:RENK.amber }}>{d.not}</div>}
        {d.satirlar.map(x => (
          <div key={x.ad} data-testid="ftl-gece-satir" style={{ fontFamily:'var(--mono)' }}>
            <span style={{ fontWeight:700 }}>{x.ad}</span> · <span style={{ color:RENK[x.renk] }}>{x.metin}</span>
          </div>
        ))}
        <div style={{ color:'var(--t3)', marginTop:4, fontSize:10 }}>
          Every night at 00:20 UTC every company's duties are recalculated by the FTL engine: a planned duty whose date
          has passed becomes CANNOT VERIFY, a missed recalculation is caught, a day off whose nights are free again comes back.
        </div>
      </div>
    </div>
  );
}
